import { useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "../admin/Modal";
import adminStyles from "../admin/Admin.module.css";
import { Dropdown } from "../shared/Dropdown";
import { downloadCsv, toCsv, type CsvColumn } from "../shared/csvExport";
import type { RankedRow } from "./organisationInsights";
import { RankedList } from "./RankedList";
import styles from "./BreakdownDialog.module.css";
import { Tooltip } from "../shared/Tooltip";

/** The orders the full list can be put in. */
type SortKey = "spend" | "change" | "people" | "perPerson" | "name";

/**
 * Every row of an Organisation panel in a dialog, for when the panel lists only the largest: the same ranked bars, which can be
 * searched by name, put in another order, and saved as a CSV file. Built on the shared {@link Modal}, so focus, Escape and the
 * backdrop behave as every other dialog in the portal.
 *
 * @param props.title - The dialog's title, which is the panel's.
 * @param props.rows - Every row, largest first.
 * @param props.currency - The currency the spend is in.
 * @param props.labelHeader - The CSV heading for a row's name (`Division`).
 * @param props.fileName - The CSV file's name.
 * @param props.onClose - Closes the dialog.
 */
export function BreakdownDialog({
  title,
  rows,
  currency,
  labelHeader,
  fileName,
  onClose,
}: {
  title: string;
  rows: RankedRow[];
  currency: string;
  labelHeader: string;
  fileName: string;
  onClose: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortKey>("spend");
  const sortId = useId();

  // Only the orders the rows can be told apart by are offered: a model has no people, a person no change.
  const sorts = useMemo(() => {
    const available: SortKey[] = ["spend"];
    if (rows.some((row) => row.change !== null)) {
      available.push("change");
    }
    if (rows.some((row) => row.people !== null)) {
      available.push("people");
    }
    if (rows.some((row) => row.perPerson !== null)) {
      available.push("perPerson");
    }
    available.push("name");
    return available;
  }, [rows]);

  const shown = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const matching = needle === "" ? rows : rows.filter((row) => row.label.toLowerCase().includes(needle));
    const valueOf = (row: RankedRow): number => {
      switch (sort) {
        case "change":
          return row.change ?? Number.NEGATIVE_INFINITY;
        case "people":
          return row.people ?? -1;
        case "perPerson":
          return row.perPerson ?? -1;
        default:
          return row.spend ?? -1;
      }
    };
    return sort === "name" ? [...matching].sort((a, b) => a.label.localeCompare(b.label)) : [...matching].sort((a, b) => valueOf(b) - valueOf(a));
  }, [rows, search, sort]);

  const exportCsv = (): void => {
    const columns: CsvColumn<RankedRow>[] = [
      { header: labelHeader, value: (row) => row.label },
      { header: t("organisation.table.spend"), value: (row) => row.spend?.toFixed(2) ?? null },
      { header: t("dataTable.currency"), value: () => currency },
      { header: t("organisation.table.share"), value: (row) => `${row.sharePercent.toFixed(1)}%` },
      ...(sorts.includes("people") ? [{ header: t("organisation.table.people"), value: (row: RankedRow) => row.people }] : []),
      ...(sorts.includes("perPerson")
        ? [{ header: t("organisation.table.averagePerPerson"), value: (row: RankedRow) => row.perPerson?.toFixed(2) ?? null }]
        : []),
      ...(sorts.includes("change")
        ? [{ header: t("organisation.ranked.changeHeader"), value: (row: RankedRow) => (row.change === null ? null : `${(row.change * 100).toFixed(0)}%`) }]
        : []),
    ];
    downloadCsv(fileName, toCsv(rows, columns));
  };

  return (
    <Modal
      title={title}
      onClose={onClose}
      wide
      tall
      lit
      closeIcon
      headerActions={
        <Tooltip label={t("dataTable.exportCsv")} placement="below" align="end">
          <button type="button" className={adminStyles.dialogIconButton} onClick={exportCsv} aria-label={t("dataTable.exportCsv")}>
            <SpreadsheetIcon />
          </button>
        </Tooltip>
      }
      toolbar={
        <>
          <div className={styles.toolbar}>
            <label className={styles.search}>
              <span className="visually-hidden">{t("organisation.ranked.search")}</span>
              <input
                type="search"
                value={search}
                placeholder={t("organisation.ranked.search")}
                onChange={(event) => setSearch(event.target.value)}
                data-autofocus
              />
            </label>
            <div className={styles.sort}>
              <label htmlFor={sortId}>{t("organisation.ranked.sortBy")}</label>
              <Dropdown
                id={sortId}
                align="end"
                value={sort}
                onChange={(value) => setSort(value as SortKey)}
                options={sorts.map((key) => ({ value: key, label: t(`organisation.ranked.sort.${key}`) }))}
              />
            </div>
          </div>
          <p className={styles.count} aria-live="polite">
            {t("organisation.ranked.showing", { shown: shown.length, count: rows.length })}
          </p>
        </>
      }
    >
      {shown.length === 0 ? (
        <p className={styles.count}>{t("organisation.ranked.noMatch")}</p>
      ) : (
        <RankedList rows={shown} currency={currency} label={title} />
      )}
    </Modal>
  );
}

/** A spreadsheet with a download arrow: the export to CSV, which opens in Excel. */
function SpreadsheetIcon(): React.JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h5" />
      <path d="M14 3v5h5M14 3l5 5v4" />
      <path d="M8 12h4M8 16h3M8 8h2" />
      <path d="M18 15v6M15.5 18.5 18 21l2.5-2.5" />
    </svg>
  );
}
